import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; 
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaCloudUploadAlt
} from 'react-icons/fa';
import { useScreenPermissions } from '../components/useScreenPermissions'; 
import { useNavigate } from 'react-router-dom';
import MaterialReceiveFormModal from './MaterialReceiveFormModal';
import MaterialReceiveViewModal from './MaterialReceiveViewModal';

// Add this component definition inside MaterialReceiveItems or at the top of the file
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = '/api'; 

// --- STYLES & THEME ---
const COLORS = {
  CLOUD: '#F5EFE6',
  BABY: '#F0ECE2',
  BABY1: '#B88A5A',
  DIVIDER: '#E6D9CC',
  BORDER: '#E6D9CC',
  TEXT: '#211f1eff',
  MUTED: '#6E584A',
  BADGE_BG: '#F6EFE4',
  SUCCESS_BG: '#EAF3EE',
  WARNING_BG: '#FBF2E8',
  PRIMARY_HOVER: '#A27246'
};

// --- ACTION MODAL ---
const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;
  let icon, title, bgColor, textColor;

  if (status === 'loading') {
    bgColor = 'bg-blue-100'; textColor = 'text-blue-600'; title = 'Processing...';
    icon = <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>;
  } else if (status === 'success') {
    bgColor = 'bg-green-100'; textColor = 'text-green-600'; title = 'Done';
    icon = <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
  } else {
    bgColor = 'bg-red-100'; textColor = 'text-red-600'; title = 'Error';
    icon = <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>;
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[71]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>{icon}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        {status === 'error' && <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Close</button>}
      </div>
    </div>
  );
};

// --- CONFIRMATION DIALOG ---
const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;
  const iconBgColor = isDanger ? 'bg-red-100' : 'bg-teal-100';
  const confirmButtonClass = isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-700 hover:bg-teal-800';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onCancel}></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center animate-in fade-in zoom-in duration-200 rounded-lg z-[61]">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
          {isDanger ? <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg> : <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium rounded text-white ${confirmButtonClass}`}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const MaterialReceiveItems = () => {
  // Theme Injection
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER, '--sidebar-bg': '#ffffff', '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1, '--accent-hover': COLORS.PRIMARY_HOVER, '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT, '--text-muted': COLORS.MUTED, '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3', '--search-icon': COLORS.MUTED, '--hover-gray': COLORS.BADGE_BG
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
  }, []);

  // Scoped CSS
  useEffect(() => {
    const styleId = 'materialreceive-overrides';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .materialreceive-root .rounded-md, .materialreceive-root .rounded-lg { border-radius: 4px !important; }
      .materialreceive-root input, .materialreceive-root select { background: #ffffff !important; border: 1px solid ${COLORS.BORDER} !important; color: ${COLORS.TEXT} !important; }
      .materialreceive-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .materialreceive-root table tbody tr { position: relative; }
      .materialreceive-root table tbody tr::after { content: ""; position: absolute; left: 0; right: 0; height: 1px; background: ${COLORS.DIVIDER}; bottom: 0; pointer-events: none; }
      .materialreceive-root .status-badge { border-radius: 9999px !important; display: inline-flex !important; align-items: center; padding: 0.25rem 0.75rem; font-size: 12px; font-weight: 600; }
    `;
    document.head.appendChild(style);
  }, []);

  // State
  const [receives, setReceives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReceive, setSelectedReceive] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // Add this state near your other useState declarations
const [selectedStatusFilters, setSelectedStatusFilters] = useState([]);

   const [successModal, setSuccessModal] = useState({
    isOpen: false,
    id: null,
    receiveNo: '',
    action: ''
  });

  // Approval State
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    receiveHeaderID: null, 
    currentLevel: null,
    remarks: ''
  });

  // Action/Confirm States
  const [actionModal, setActionModal] = useState({ isOpen: false, status: 'loading', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, isDanger: false });

  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  const navigate = useNavigate();

  // Sorting & Pagination
  const [sortConfig, setSortConfig] = useState({ key: 'CreatedAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Fetch Data
  const fetchMaterialReceives = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-receive-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setReceives(Array.isArray(data) ? data : []);
    } catch (error) {
      // console.error('Error fetching Material Receives:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMaterialReceives(); }, []);

  // Handlers
  const handleCreate = () => {
    setSelectedReceive(null);
    setShowFormModal(true);
  };

  const handleEdit = async (receive) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-receive/${receive.ReceiveHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedReceive(fullData);
      setShowFormModal(true);
    } catch (error) {
      // console.error('Error fetching details:', error);
      setActionModal({ isOpen: true, status: 'error', message: 'Could not load record details' });
    }
  };

  const handleView = async (receive) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-receive/${receive.ReceiveHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedReceive(fullData);
      setShowViewModal(true);
    } catch (error) {
      // console.error('Error fetching view details:', error);
      setActionModal({ isOpen: true, status: 'error', message: 'Could not load record details' });
    }
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Material Receive',
      message: 'Are you sure you want to delete this Material Receive?',
      isDanger: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/material-receive/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const result = await response.json();

        if (!response.ok) {
          showToast(
            result.message || 'Delete not allowed',
            'error',
            'Action Not Allowed'
          );
          return;
        }

          // if (!response.ok) throw new Error('Failed to delete');
          fetchMaterialReceives();
          // setActionModal({ isOpen: true, status: 'success', message: 'Deleted successfully' });
          // setTimeout(() => setActionModal({ isOpen: false }), 2000);

         showToast('Deleted successfully', 'success', 'Done');
        } catch (error) {
          // console.error('Error deleting:', error);
          // setActionModal({ isOpen: true, status: 'error', message: 'Error deleting record' });
          showToast('Error deleting record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  const handleSendToBc = (receive) => {
    if (receive.SendToBC) {
      setActionModal({ isOpen: true, status: 'error', message: 'This Receive has already been sent to BC.' });
      return;
    }
    setConfirmModal({
      isOpen: true, title: 'Send to BC', message: `Send Receive ${receive.ReceiveHeaderNo} to BC?`, isDanger: false,
      onConfirm: async () => {
        setActionModal({ isOpen: true, status: 'loading', message: 'Sending to Business Central...' });
        try {
          const token = localStorage.getItem('authToken');

          // --- START UPDATE ---
        const resourceType = receive.ReceiveSource ; // Default to Issue if undefined
        let url = `${API_URL}/mapping/material-receive/issue/send-to-bc`; // Default URL for Issue

        // If Resource is Return, change the URL
        if (resourceType === 'Return') {
            url = `${API_URL}/mapping/material-receive/return/send-to-bc`; // <--- UPDATE THIS URL TO YOUR RETURN API ENDPOINT
        }

          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                receiveHeaderID: receive.ReceiveHeaderID,
                Resource: resourceType // Sending Issue/Return to verify
            })
        });

           // ✅ FIX: Parse the JSON body regardless of success or error
          const result = await response.json();

          // ✅ FIX: Check status, and throw the specific message from the backend
          if (!response.ok) {
             throw new Error(result.message || 'Failed to send to BC');
          }
          fetchMaterialReceives();
          setActionModal({ isOpen: true, status: 'success', message: 'Sent to BC successfully!' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Sent to BC successfully', 'success', 'Done');
        } catch (error) {
          setActionModal({ isOpen: true, status: 'error', message: 'Error sending record: ' + error.message });
          // showToast('Error sending record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  const handlePostToBc = (receive) => {
    if (receive.PostingSiteIndent) {
      setActionModal({ isOpen: true, status: 'error', message: 'This Receive has already been Posted to BC.' });
      return;
    }
    if (!receive.SendToBC) {
      setActionModal({ isOpen: true, status: 'error', message: 'Please Send to BC before Posting.' });
      return;
    }
    setConfirmModal({
      isOpen: true, title: 'Post to BC', message: `Post Site Indent ${receive.SiteIndentNo} to BC?`, isDanger: false,
      onConfirm: async () => {
        setActionModal({ isOpen: true, status: 'loading', message: 'Posting to Business Central...' });
        try {
          const token = localStorage.getItem('authToken');
          const resourceType = receive.ReceiveSource ;
        let url = `${API_URL}/mapping/material-receive/issue/post-to-bc`; // Default URL for Issue

        // If Resource is Return, change the URL
        if (resourceType === 'Return') {
            url = `${API_URL}/mapping/material-receive/return/post-to-bc`; // <--- UPDATE THIS URL TO YOUR RETURN API ENDPOINT
        }
          const payload = {
            documentNo: receive.ReceiveHeaderID,
            Resource: resourceType, // Sending Issue/Return to verify
            Issue: "False",
            Receive: "True"
          };

           const response = await fetch(url, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

          // ✅ FIX: Parse response first
          const result = await response.json();

          // ✅ FIX: Throw specific error
          if (!response.ok) {
             throw new Error(result.message || 'Failed to post to BC');
          }
          fetchMaterialReceives();
          setActionModal({ isOpen: true, status: 'success', message: 'Posted to BC successfully!' });
          setTimeout(() => setActionModal({ isOpen: false }), 2000);
          // showToast('Posted to BC successfully', 'success', 'Done');
        } catch (error) {
          setActionModal({ isOpen: true, status: 'error', message: 'Error posting record: ' + error.message });
          // showToast('Error posting record: ' + error.message, 'error', 'Error');
        }
      }
    });
  };

  // Add this handler function
const handleStatusFilterChange = (filterValue) => {
  setSelectedStatusFilters(prev => 
    prev.includes(filterValue) 
      ? prev.filter(item => item !== filterValue) 
      : [...prev, filterValue]
  );
};

  // Approval Handlers
  const openApprovalModal = (receiveHeaderID, level) => {
    setApprovalModal({
      isOpen: true,
      receiveHeaderID: receiveHeaderID,
      currentLevel: level,
      remarks: ''
    });
  };

  const showToast = (message, type = 'success', title = 'Done', duration = 4000) => {
    setToast({ open: true, message, type, title });
    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  const handleApprovalAction = async (action) => {
    const { receiveHeaderID, remarks } = approvalModal;

    if (action === 'REJECTED' && !remarks.trim()) {
      // alert('Remarks are mandatory when rejecting.');
      showToast('Remarks are mandatory when rejecting.', 'error', 'Error');
      return;
    }
    if (action === 'APPROVED' && !remarks.trim()) {
      // alert('Remarks are mandatory when approving.');
      showToast('Remarks are mandatory when approving.', 'error', 'Error');
      return;
    }

    setApprovalModal({ ...approvalModal, isOpen: false });
    setActionModal({ isOpen: true, status: 'loading', message: `${action === 'APPROVED' ? 'Approving' : 'Rejecting'} Material Receive...` });

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = action === 'APPROVED' 
        ? `${API_URL}/material-receive/approve` 
        : `${API_URL}/material-receive/reject`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Using receiveHeaderID to match Material Receive naming convention
        body: JSON.stringify({ receiveHeaderID, remarks })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Action failed');
      }
      
      fetchMaterialReceives();
      setActionModal({ isOpen: true, status: 'success', message: `Material Receive ${action === 'APPROVED' ? 'Approved' : 'Rejected'} successfully` });
      setTimeout(() => setActionModal({ isOpen: false }), 2000);
    } catch (error) {
      // console.error(`Error:`, error);
      setActionModal({ isOpen: true, status: 'error', message: `Error: ${error.message}` });
    }
  };


  // Handler to trigger success message
  const handleFormSuccess = (result, actionType) => {
    // Delay slightly to let the form closing animation start
    setTimeout(() => {
      setSuccessModal({
        isOpen: true,
        id: result.ReceiveHeaderID,
        receiveNo: result.ReceiveHeaderNo,
        action: actionType // 'Created' or 'Updated'
      });
    }, 300); // 300ms delay
  };

  // Handler to close the success message
  const handleCloseSuccess = () => {
    setSuccessModal({ isOpen: false, id: null, receiveNo: '', action: '' });
    fetchMaterialReceives(); // Refresh the list
  };

  // Auto-close success modal after 2 seconds
  useEffect(() => {
    if (successModal.isOpen) {
      const timer = setTimeout(() => {
        handleCloseSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen]);

  // Filtering Logic
    // Filtering Logic
  const filteredReceives = receives.filter(receive => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = [
      receive.ReceiveHeaderNo,
      receive.ReceiveSourceNo,
      receive.ProjectNo,
      receive.ProjectDescription,
      receive.VendorCode,
      receive.VendorName,
      receive.ToFeederCode,
      receive.ToFeederDescription
    ].some(field => field?.toString().toLowerCase().includes(term));
    
    const matchesStatus = statusFilter === 'All' || receive.Status === statusFilter;

    // --- ADDED: Process Status Filter Logic (Including Post Site Indent) ---
    const matchesBcStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.some(filter => {
      
      // 1. Approval Phase
      if (filter === 'Pending') return receive.ApprovalStatus === 'PENDING';
      if (filter === 'Approved') return receive.ApprovalStatus === 'APPROVED';
      if (filter === 'Rejected') return receive.ApprovalStatus === 'REJECTED';

      // 2. BC Integration Phase
      // Ready to Send to BC (Approved but not sent)
      if (filter === 'Send to BC')
        return (
          !receive.SendToBC &&
          (receive.ApprovalStatus === 'APPROVED' || receive.ApprovalStatus == null)
        );

      // 3. The NEW Status: Post Site Indent (Sent, but not yet Posted)
      if (filter === 'Post Site Indent')
        return (
          receive.SendToBC === true && 
          !receive.PostingSiteIndent
        );

      // 4. Final Success State
      if (filter === 'Posted Successfully') return receive.PostingSiteIndent === true;

      return false;
    });
    // --------------------------------------------------------------

    return matchesSearch && matchesStatus && matchesBcStatus;
  });

  // Sorting Logic
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let valA = a[sortConfig.key] || '';
      let valB = b[sortConfig.key] || '';
      if (sortConfig.key === 'ReceiveDate' || sortConfig.key === 'CreatedAt') {
        valA = new Date(valA); valB = new Date(valB);
      } else {
        valA = valA.toString().toLowerCase(); valB = valB.toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedReceives = getSortedData(filteredReceives);
  const totalPages = Math.ceil(sortedReceives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedReceives.slice(startIndex, startIndex + itemsPerPage);

  const renderSortArrows = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-xs ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="text-xs ml-1" /> : <FaSortDown className="text-xs ml-1" />;
  };

  const canCreate = hasFullAccess || hasPermission('MATERIAL_RECEIVE', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MATERIAL_RECEIVE', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MATERIAL_RECEIVE', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MATERIAL_RECEIVE', 'CanDelete');

  // 🔒 Business rules
// const isEditLocked = (receive) =>
//   receive.CurrentLevel >= 2 || (receive.CurrentLevel === 1 &&
//     ['APPROVED', 'REJECTED'].includes(receive.ApprovalStatus)) || receive.SendToBC === true ||
//   receive.PostingSiteIndent === true;

// const isDeleteLocked = (receive) =>
//   receive.ApprovalStatus === 'APPROVED' ||
//   receive.SendToBC === true || receive.PostingSiteIndent === true;

  
   // 🔒 MATERIAL RECEIVE LOCK RULES
  
  // --- EDIT LOGIC: Updated ---
  const isEditLocked = (receive) => {
    // 1. If Sent to BC or Posted, always Locked
    if (receive.SendToBC === true || receive.PostingSiteIndent === true) return true;

    // 2. If Rejected, always Locked
    if (receive.ApprovalStatus === 'REJECTED') return true;
    
    if (!receive.ApprovalFlowID && !receive.SendToBC) {
      return false;
    }

    // 3. If Fully Approved (All levels done), Locked for EVERYONE
    // This ensures neither entry user nor approvers can edit after final approval.
    if (receive.ApprovalStatus === 'APPROVED') return true;


    // 4. If Pending Approval
    if (receive.ApprovalStatus === 'PENDING' && receive.CurrentLevel > 1) {
      // 'CanCurrentUserApprove' is calculated in SQL.
      // It is TRUE only if the logged-in user matches the CurrentApprover of the active level.
      // It restricts edit rights to ONLY the current level's approver.
      if (receive.CanCurrentUserApprove) {
        return false; // Unlocked for this specific approver
      }
      return true; // Locked for Entry User, Previous Approvers, and everyone else
    }

    // 5. Default (Draft/No Flow) - Unlocked
    return false;
  };

  
  const isDeleteLocked = (receive) => {
    // "Same case with delete"
    
    // 1. If Rejected, always locked
    // if (item.ApprovalStatus === 'REJECTED') return true;

    // 2. If Current Level > 1, locked
    if (receive.CurrentLevel > 1) return true;

    if (receive.ApprovalStatus === 'APPROVED' && receive.DocumentApprovalID) {
      return true;
    }

    // 3. If Level 1 is Approved, locked
    if (receive.CurrentLevel === 1 && receive.ApprovalStatus === 'APPROVED') return true;

    // 4. Specific Logic: "Approved but no DocumentApprovalID"
    if (receive.ApprovalStatus === 'APPROVED' && !receive.DocumentApprovalID) {
      return receive.SendToBC === true || receive.PostingSiteIndent === true;
    }

    // 5. Standard Lock: If Sent to BC or Validated
    if (receive.SendToBC === true || receive.PostingSiteIndent === true) return true;

    return false;
  };


  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex materialreceive-root">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }} onLogout={() => navigate('/login')} />
      
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Material Receive Management</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Receive materials based on Material Issues.</p>
            </div>
            {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm" style={{ backgroundColor: COLORS.BABY1, color: '#fff' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER} onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}>
                <FaPlus className="w-4 h-4" /> Create Receive
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
                {/* Filters */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <input type="text" placeholder="Receive No, Project, Issue No..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full px-3 py-2 bg-white border focus:outline-none" style={{ borderColor: COLORS.BORDER }} />
            </div>
            
            
            {/* --- ADD THIS BLOCK HERE --- */}
            <div className="">
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.MUTED }}>Process Status</label>
              <div className="flex flex-wrap gap-4 text-sm px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
                {['Pending', 'Approved', 'Rejected', 'Send to BC', 'Post Site Indent', 'Posted Successfully'].map((label) => (
                  <label key={label} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      checked={selectedStatusFilters.includes(label)}
                      onChange={() => handleStatusFilterChange(label)}
                    />
                    <span style={{ color: COLORS.TEXT }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* --------------------------- */}

            {/* <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 bg-white border focus:outline-none w-full md:w-auto" style={{ borderColor: COLORS.BORDER }}>
                <option value="All">All Status</option>
                <option value="Open">Open</option>
                <option value="Released">Released</option>
                <option value="Closed">Closed</option>
              </select>
            </div> */}

          </div>
        </div>
        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Material Receive List</h2>
            <div className="flex items-center space-x-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2" style={{color: currentPage === 1 ? '#ccc' : COLORS.TEXT}}><FaChevronLeft /></button>
              <span style={{color: COLORS.TEXT}}>{currentPage} / {totalPages || 1}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-2" style={{color: currentPage === totalPages || totalPages === 0 ? '#ccc' : COLORS.TEXT}}><FaChevronRight /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : currentItems.length === 0 ? <div className="text-center py-8 text-gray-500">No records found</div> : (
              <table className="w-full">
                <thead className="text-xs font-semibold text-left uppercase" style={{ backgroundColor: COLORS.BABY, color: COLORS.TEXT, borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'ReceiveHeaderNo', direction: sortConfig.key === 'ReceiveHeaderNo' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>Receive No {renderSortArrows('ReceiveHeaderNo')}</th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => setSortConfig({ key: 'ReceiveDate', direction: sortConfig.key === 'ReceiveDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}>Date {renderSortArrows('ReceiveDate')}</th>
                    <th className="px-4 py-3">Docuement No</th>
                    <th className="px-4 py-3">Project</th>
                     <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">To Feeder</th>
                    {/* <th className="px-4 py-3">Status</th> */}
                    <th className="px-4 py-3">Approval/BC Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((receive, idx) => (
                    <tr key={receive.ReceiveHeaderID || idx} className="hover:bg-[#faf9f8] transition-colors">
                      <td className="px-4 py-3 font-medium" style={{ color: COLORS.TEXT }}>{receive.ReceiveHeaderNo}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{formatDate(receive.ReceiveDate)}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>{receive.ReceiveSourceNo || '-'}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{receive.ProjectNo}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{receive.ProjectDescription || '-'}</div>
                      </td>
                      <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{receive.VendorCode || '-'}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{receive.VendorName || '-'}</div>
                      </td>
                       <td className="px-4 py-3" style={{ color: COLORS.MUTED }}>
                        <div className="font-medium text-black">{receive.ToFeederCode}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{receive.ToFeederDescription || '-'}</div>
                      </td>
                      {/* <td className="px-4 py-3">
                        <span className={`status-badge ${receive.Status === 'Open' ? 'bg-green-100 text-green-800' : receive.Status === 'Released' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {receive.Status || 'Draft'}
                        </span>
                      </td> */}
                      {/* Approval Logic */}
                      <td className='px-4 py-3'>
                        {receive.ApprovalStatus === 'PENDING' ? (
                          receive.CanCurrentUserApprove ? (
                            <button 
                              onClick={() => openApprovalModal(receive.ReceiveHeaderID, receive.CurrentLevel)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded text-white transition-colors"
                              style={{ backgroundColor: '#16A34A' }}
                            >
                              Approve (Lvl {receive.CurrentLevel})
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500 italic flex items-center gap-1">
                              Approval is going on (Lvl {receive.CurrentLevel})
                            </span>
                          )
                        ) : receive.ApprovalStatus === 'REJECTED' ? (
                          <span className="text-xs font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-1 rounded border border-red-200">Rejected</span>
                        ) : (
                          <>
                            {receive.PostingSiteIndent ? (
                              <span className="text-xs font-semibold text-green-600 flex items-center gap-1 bg-purple-50 px-2 py-1 rounded border border-green-200">Material received successfully</span>
                            ) : receive.SendToBC ? (
                              <button onClick={() => handlePostToBc(receive)} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 flex items-center gap-1"><FaCloudUploadAlt className="text-xs" /> Post to BC</button>
                            ) : (
                              <button onClick={() => handleSendToBc(receive)} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">Send to BC</button>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center space-x-3" style={{ color: COLORS.MUTED }}>
                          {canView && (<button onClick={() => handleView(receive)} title="View" className="hover:text-black"><FaEye /></button>)}
                          {canEdit && (
  <button
    onClick={() => !isEditLocked(receive) && handleEdit(receive)}
    disabled={isEditLocked(receive)}
    title={isEditLocked(receive) ? 'Edit disabled after approval' : 'Edit'}
    className={`${
      isEditLocked(receive)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-blue-600'
    }`}
  >
    <FaEdit />
  </button>
)}

                         {canDelete && (
  <button
    onClick={() =>
      !isDeleteLocked(receive) &&
      handleDelete(receive.ReceiveHeaderID)
    }
    disabled={isDeleteLocked(receive)}
    title={
      isDeleteLocked(receive)
        ? 'Delete disabled after validation'
        : 'Delete'
    }
    className={`${
      isDeleteLocked(receive)
        ? 'cursor-not-allowed opacity-40'
        : 'hover:text-red-600'
    }`}
  >
    <FaTrash />
  </button>
)}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>

      {/* MODALS */}
      {showFormModal && (
        <MaterialReceiveFormModal
          open={showFormModal}
          onClose={() => setShowFormModal(false)}
          initialData={selectedReceive}
          onSubmit={fetchMaterialReceives}
           onFormSuccess={handleFormSuccess}
        />
      )}

      {showViewModal && selectedReceive && (
        <MaterialReceiveViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          data={selectedReceive}
        />
      )}

      {/* --- APPROVAL/REJECT MODAL --- */}
      {approvalModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50">
          <div className="relative bg-white shadow-xl max-w-md w-full mx-4 p-6 rounded-lg animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Review Material Receive: {approvalModal.receiveHeaderID} (Level {approvalModal.currentLevel})
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Enter remarks..."
                value={approvalModal.remarks}
                onChange={(e) => setApprovalModal({ ...approvalModal, remarks: e.target.value })}
              ></textarea>
            </div>

            <div className="flex justify-between gap-3">
              <button
                onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprovalAction('REJECTED')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprovalAction('APPROVED')}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* --- SUCCESS MODAL (Moved from Child) --- */}
      {successModal.isOpen && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-slideDown">
          <div className="flex items-center gap-4 bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 min-w-[420px]">
            {/* Icon */}
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <SuccessIcon size={22} />
            </div>

            {/* Text */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-800">
                Material Receive {successModal.action} Successfully
              </h4>
              <p className="text-xs text-gray-500">
                Receive No: <span className="font-medium">{successModal.receiveNo}</span>
              </p>
            </div>

            {/* Loader bar */}
            <div className="absolute bottom-0 left-0 h-[3px] bg-green-500 rounded-b-xl animate-progress" />
          </div>

          <style>
            {`
              @keyframes slideDown {
                from { opacity: 0; transform: translate(-50%, -10px); }
                to { opacity: 1; transform: translate(-50%, 0); }
              }
              @keyframes progress {
                from { width: 100%; }
                to { width: 0%; }
              }
              .animate-slideDown { animation: slideDown 0.25s ease-out; }
              .animate-progress { animation: progress 2s linear; }

            `}
          </style>
        </div>
      )}

        {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div className={`
            relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
            ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
            ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
            ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
          `}>
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}
                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>
              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">{toast.title}</div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{toast.message}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
          <style>
            {`
             
                /* Add this to the end of your existing style.innerHTML string */
                @keyframes toastIn {
                  from { opacity: 0; transform: translate(-50%, -16px) scale(0.98); }
                  to { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes toastProgress {
                  from { width: 100%; }
                  to { width: 0%; }
                }
                .animate-toastIn { animation: toastIn 0.25s ease-out; }
                .animate-toastProgress { animation: toastProgress 4s linear forwards; }

            `}
          </style>
        </div>
      )}

      <ActionModal isOpen={actionModal.isOpen} status={actionModal.status} message={actionModal.message} onClose={() => setActionModal({ ...actionModal, isOpen: false })} />
      <ConfirmationDialog isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} onConfirm={() => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); }} onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} isDanger={confirmModal.isDanger} />
    </div>
  );
};

export default MaterialReceiveItems;